/**
 * Zentraler API-Fetch mit automatischem Token-Refresh bei 401.
 *
 * Wenn ein Request mit 401 fehlschlägt, wird automatisch versucht den
 * Token per Refresh-Token zu erneuern und den Request zu wiederholen.
 * Schlägt auch der Refresh fehl, wird der User ausgeloggt.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

function getStoredTokens() {
  const stored = localStorage.getItem('authTokens');
  return stored ? JSON.parse(stored) : null;
}

function getAccessToken(): string | null {
  return getStoredTokens()?.access_token ?? null;
}

async function refreshAccessToken(): Promise<boolean> {
  const tokens = getStoredTokens();
  if (!tokens?.refresh_token) return false;

  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: tokens.refresh_token }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    if (data.access_token) {
      const newTokens = { ...tokens, access_token: data.access_token };
      if (data.refresh_token) newTokens.refresh_token = data.refresh_token;
      localStorage.setItem('authTokens', JSON.stringify(newTokens));
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Fetch mit automatischem 401-Retry.
 * Verwendung wie normales fetch(), aber relative Pfade werden zum API_BASE_URL aufgelöst.
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const doFetch = () => {
    const token = getAccessToken();
    const headers: Record<string, string> = {
      ...(init?.headers as Record<string, string> || {}),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // Content-Type nur fuer Plain-String-Bodies setzen. Bei FormData /
    // Blob / URLSearchParams setzt der Browser den Header inkl. der
    // korrekten Boundary selbst — manuelles 'application/json' wuerde
    // Multipart-Uploads kaputt machen (Audit 3 Bug 2). Header-Lookup
    // case-insensitive, sonst wird existierendes 'content-type' uebersehen.
    const hasCT = Object.keys(headers).some(k => k.toLowerCase() === 'content-type');
    const body = init?.body;
    const isStringBody = typeof body === 'string';
    if (!hasCT && isStringBody) {
      headers['Content-Type'] = 'application/json';
    }

    return fetch(url, { ...init, headers });
  };

  // Erster Versuch
  let res = await doFetch();

  // Bei 401: Token refreshen und nochmal versuchen
  if (res.status === 401) {
    // Nur ein Refresh gleichzeitig — andere Requests warten
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }

    const refreshed = await (refreshPromise ?? refreshAccessToken());

    if (refreshed) {
      // Retry mit neuem Token
      res = await doFetch();
    } else {
      // Refresh fehlgeschlagen — ausloggen
      localStorage.removeItem('authTokens');
      window.location.href = '/login';
    }
  }

  return res;
}

/**
 * Extrahiert eine lesbare Fehlermeldung aus einer API-Antwort.
 * FastAPI/Pydantic gibt `detail` als Array von Objekten zurück bei Validierungsfehlern,
 * was React nicht als Child rendern kann (Error #31).
 */
export function extractErrorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail ?? err?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ') || fallback;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (typeof err?.message === 'string' && err.message !== '') {
    return err.message;
  }
  return fallback;
}

/**
 * Typisierter API-Aufruf — gibt JSON zurück oder wirft einen Fehler.
 */
export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `Fehler: HTTP ${res.status}`);
  }
  return res.json();
}
